// frontend/src/admin/PaymentConfigModal.js
// ════════════════════════════════════════════════════════════════════════
// VERSIÓN SIMPLIFICADA - PayPal.me (solo username)
// ════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import { FaQrcode, FaUniversity, FaCheckCircle, FaTimes, FaPaypal, FaInfoCircle, FaExternalLinkAlt, FaQuestionCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';
import './PaymentConfigModal.css';

const PaymentConfigModal = ({ show, onHide, onConfigSaved }) => {
    const [activeTab, setActiveTab] = useState('paypal');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Estados para QR
    const [qrFile, setQrFile] = useState(null);
    const [qrPreview, setQrPreview] = useState(null);
    const [qrBank, setQrBank] = useState('');

    // Estados para cuenta bancaria
    const [bankName, setBankName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');

    // Estados para PayPal.me (simplificado)
    const [paypalMeUsername, setPaypalMeUsername] = useState('');
    const [paypalConfigured, setPaypalConfigured] = useState(false);
    const [paypalConfiguredAt, setPaypalConfiguredAt] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [showPayPalHelp, setShowPayPalHelp] = useState(false);

    const BANCOS = [
        'Banco Pichincha',
        'Banco Produbanco',
        'Banco del Pacífico',
        'Banco de Guayaquil',
        'Banco Bolivariano',
        'Banco Internacional',
        'Banco Solidario',
        'Cooperativa JEP',
        'BanEcuador'
    ];

    // Cargar configuración PayPal existente
    useEffect(() => {
        if (show) {
            loadPayPalConfig();
        }
    }, [show]);

    const loadPayPalConfig = async () => {
        setLoadingConfig(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(API_ENDPOINTS.condominiumPayPalConfig, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success && data.data.configured) {
                setPaypalConfigured(true);
                setPaypalConfiguredAt(data.data.configuredAt);
                // Cargar username existente si está guardado
                if (data.data.paypalMeUsername) {
                    setPaypalMeUsername(data.data.paypalMeUsername);
                }
            } else {
                setPaypalConfigured(false);
            }
        } catch (err) {
            console.error('Error cargando config PayPal:', err);
        } finally {
            setLoadingConfig(false);
        }
    };

    // Resetear formulario al cerrar
    useEffect(() => {
        if (!show) {
            setError(null);
            setSuccess(false);
            setPaypalMeUsername('');
            setShowPayPalHelp(false);
            setQrFile(null);
            setQrPreview(null);
            setQrBank('');
            setBankName('');
            setAccountNumber('');
        }
    }, [show]);

    // Manejar selección de archivo QR
    const handleQrFileChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
            setError('Solo se permiten archivos JPG, JPEG o PNG');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setError('El archivo no debe superar 2MB');
            return;
        }

        setQrFile(file);
        setError(null);

        const reader = new FileReader();
        reader.onloadend = () => {
            setQrPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveQr = () => {
        setQrFile(null);
        setQrPreview(null);
        const fileInput = document.getElementById('qrFileInput');
        if (fileInput) fileInput.value = '';
    };

    // Validar formulario
    const validateForm = () => {
        setError(null);

        if (activeTab === 'qr') {
            if (!qrFile) {
                setError('Debes seleccionar una imagen de código QR');
                return false;
            }
            if (!qrBank) {
                setError('Debes seleccionar el banco del código QR');
                return false;
            }
        } else if (activeTab === 'bank') {
            if (!bankName) {
                setError('Debes seleccionar un banco');
                return false;
            }
            if (!accountNumber || accountNumber.length < 10) {
                setError('El número de cuenta debe tener al menos 10 dígitos');
                return false;
            }
        } else if (activeTab === 'paypal') {
            if (!paypalMeUsername || !paypalMeUsername.trim()) {
                setError('Debes ingresar tu usuario de PayPal.me');
                return false;
            }
            // Validar que no tenga espacios ni caracteres especiales
            const usernameClean = paypalMeUsername.trim();
            if (!/^[a-zA-Z0-9._-]+$/.test(usernameClean)) {
                setError('El usuario de PayPal.me solo puede contener letras, números, puntos, guiones y guiones bajos');
                return false;
            }
            if (usernameClean.length < 3) {
                setError('El usuario de PayPal.me debe tener al menos 3 caracteres');
                return false;
            }
        }
        return true;
    };

    // Enviar configuración
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No autenticado');

            if (activeTab === 'paypal') {
                // Guardar configuración PayPal.me (simplificado)
                console.log('Guardando configuración PayPal.me...');

                const response = await fetch(API_ENDPOINTS.condominiumPayPalConfig, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        paypalMeUsername: paypalMeUsername.trim()
                    })
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Error al guardar configuración de PayPal.me');
                }

                console.log('✅ PayPal.me configurado correctamente');

            } else {
                // Guardar QR o cuenta bancaria
                const formData = new FormData();

                if (activeTab === 'qr') {
                    formData.append('qr_image', qrFile);
                    formData.append('bank_name', qrBank);
                    formData.append('is_primary', 'true');
                } else {
                    formData.append('bank_name', bankName);
                    formData.append('account_number', accountNumber);
                    formData.append('is_primary', 'true');
                }

                const response = await fetch(API_ENDPOINTS.paymentConfig, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Error al guardar configuración');
                }
            }

            setSuccess(true);

            setTimeout(() => {
                if (onConfigSaved) onConfigSaved();
                onHide();
            }, 2000);

        } catch (err) {
            console.error('Error al guardar configuración:', err);
            setError(err.message || 'Error al guardar configuración');
        } finally {
            setLoading(false);
        }
    };

    // Renderizar tab de PayPal.me simplificado
    const renderPayPalTab = () => (
        <div className="paypal-config-container">
            {/* Logo de PayPal */}
            <div className="paypal-logo-container">
                <img
                    src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-200px.png"
                    alt="PayPal"
                    className="paypal-logo"
                />
                <div className="paypal-me-badge">
                    <span>.me</span>
                </div>
            </div>

            {/* Estado de configuración */}
            {paypalConfigured && (
                <div className="paypal-status-badge configured">
                    <FaCheckCircle className="me-2" />
                    Configurado {paypalConfiguredAt && `el ${new Date(paypalConfiguredAt).toLocaleDateString('es-EC')}`}
                </div>
            )}

            {/* Descripción simple */}
            <div className="paypal-info-box">
                <FaInfoCircle className="info-icon" />
                <div className="info-content">
                    <strong>¿Cómo funciona?</strong>
                    <p>Los residentes serán redirigidos a tu enlace PayPal.me para pagar. Después suben su comprobante y tú lo validas.</p>
                </div>
            </div>

            {/* Ayuda colapsable - Cómo crear PayPal.me */}
            <div className="paypal-help-section">
                <button
                    type="button"
                    className="paypal-help-toggle"
                    onClick={() => setShowPayPalHelp(!showPayPalHelp)}
                >
                    <FaQuestionCircle className="me-2" />
                    ¿No tienes PayPal.me?
                    {showPayPalHelp ? <FaChevronUp className="ms-auto" /> : <FaChevronDown className="ms-auto" />}
                </button>

                {showPayPalHelp && (
                    <div className="paypal-help-content">
                        <p className="help-intro">Créalo gratis en 1 minuto:</p>
                        <ol className="help-steps">
                            <li>Ve a <strong>paypal.com/paypalme</strong></li>
                            <li>Haz clic en <strong>"Crear perfil"</strong></li>
                            <li>Elige tu nombre de usuario</li>
                            <li>¡Listo! Copia ese nombre aquí</li>
                        </ol>
                        <a
                            href="https://www.paypal.com/paypalme"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="paypal-help-btn"
                        >
                            <FaExternalLinkAlt className="me-2" />
                            Crear mi PayPal.me
                        </a>
                    </div>
                )}
            </div>

            {/* Formulario simplificado - SOLO UN CAMPO */}
            <div className="paypal-form">
                <Form.Group className="mb-4">
                    <Form.Label className="paypal-label">
                        Usuario de PayPal.me
                    </Form.Label>
                    <div className="paypal-me-input-wrapper">
                        <span className="paypal-me-prefix">paypal.me/</span>
                        <Form.Control
                            type="text"
                            placeholder="tunombre o tucorreo"
                            value={paypalMeUsername}
                            onChange={(e) => setPaypalMeUsername(e.target.value.replace(/\s/g, ''))}
                            disabled={loading}
                            className="paypal-me-input"
                        />
                    </div>
                    <Form.Text className="text-muted">
                        Ejemplo: si tu link es paypal.me/juanperez, escribe <strong>juanperez</strong>
                    </Form.Text>
                </Form.Group>

                {/* Preview del link */}
                {paypalMeUsername && (
                    <div className="paypal-me-preview">
                        <span className="preview-label">Tu enlace de pago será:</span>
                        <a
                            href={`https://paypal.me/${paypalMeUsername}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="preview-link"
                        >
                            <FaExternalLinkAlt className="me-2" />
                            paypal.me/{paypalMeUsername}
                        </a>
                    </div>
                )}
            </div>

        </div>
    );

    return (
        <Modal
            show={show}
            onHide={loading ? null : onHide}
            backdrop="static"
            keyboard={!loading}
            size="lg"
            centered
            className="payment-config-modal"
        >
            <Modal.Header closeButton={!loading} className="bg-primary text-white">
                <Modal.Title>
                    <FaQrcode className="me-2" />
                    Configuración de Métodos de Pago
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert variant="success">
                        <FaCheckCircle className="me-2" />
                        ¡Configuración guardada exitosamente!
                    </Alert>
                )}

                {loadingConfig ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Cargando configuración...</p>
                    </div>
                ) : !success && (
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4 payment-tabs">

                        {/* Tab PayPal */}
                        <Tab eventKey="paypal" title={<span className="tab-title"><FaPaypal className="me-1" /> PayPal</span>}>
                            {renderPayPalTab()}
                        </Tab>

                        {/* Tab de Código QR */}
                        <Tab eventKey="qr" title={<span className="tab-title"><FaQrcode className="me-1" /> Código QR</span>}>
                            <div className="p-3">
                                <Form.Group className="mb-3">
                                    <Form.Label><strong>Banco</strong></Form.Label>
                                    <Form.Select
                                        value={qrBank}
                                        onChange={(e) => setQrBank(e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="">-- Selecciona el banco --</option>
                                        {BANCOS.map(banco => (
                                            <option key={banco} value={banco}>{banco}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label><strong>Imagen del Código QR</strong></Form.Label>

                                    {!qrPreview ? (
                                        <div className="qr-upload-area">
                                            <input
                                                type="file"
                                                id="qrFileInput"
                                                accept="image/jpeg,image/jpg,image/png"
                                                onChange={handleQrFileChange}
                                                disabled={loading}
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="qrFileInput" className="qr-upload-label">
                                                <FaQrcode size={48} className="mb-2 text-primary" />
                                                <p className="mb-0"><strong>Haz clic para subir</strong></p>
                                                <small className="text-muted">JPG, JPEG o PNG (máx. 2MB)</small>
                                            </label>
                                        </div>
                                    ) : (
                                        <div className="qr-preview-container">
                                            <img src={qrPreview} alt="Preview QR" className="qr-preview-image" />
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                className="qr-remove-btn"
                                                onClick={handleRemoveQr}
                                                disabled={loading}
                                            >
                                                <FaTimes />
                                            </Button>
                                        </div>
                                    )}
                                </Form.Group>
                            </div>
                        </Tab>

                        {/* Tab de Cuenta Bancaria */}
                        <Tab eventKey="bank" title={<span className="tab-title"><FaUniversity className="me-1" /> Cuenta Bancaria</span>}>
                            <div className="p-3">
                                <Form.Group className="mb-3">
                                    <Form.Label><strong>Banco</strong></Form.Label>
                                    <Form.Select
                                        value={bankName}
                                        onChange={(e) => setBankName(e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="">-- Selecciona el banco --</option>
                                        {BANCOS.map(banco => (
                                            <option key={banco} value={banco}>{banco}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group>
                                    <Form.Label><strong>Número de Cuenta</strong></Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ingresa el número de cuenta"
                                        value={accountNumber}
                                        onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                                        disabled={loading}
                                        maxLength={20}
                                    />
                                    <Form.Text className="text-muted">
                                        Solo números, mínimo 10 dígitos
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </Tab>
                    </Tabs>
                )}
            </Modal.Body>

            <Modal.Footer>
                {!success && (
                    <>
                        <Button variant="secondary" onClick={onHide} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={loading || loadingConfig}>
                            {loading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    {activeTab === 'paypal' ? 'Validando...' : 'Guardando...'}
                                </>
                            ) : (
                                <>
                                    <FaCheckCircle className="me-2" />
                                    Guardar Configuración
                                </>
                            )}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default PaymentConfigModal;
